import { IconButton, RichInput, RichTextValue, SuggestionsItemData } from "@xapp/chat-widget-core";
import { WidgetFooterConfig, WidgetInputConfig } from "@xapp/stentor-chat-widget";
import React, { DragEvent, FC, FormEvent, KeyboardEvent, useCallback, useState } from "react";
import { CloseIcon } from "../Icons";
import { SendButton } from "../SendButton";
import "./Input.scss";

export type SuggestionCommand = "prev" | "next" | "cancel";

export interface Props {
    readonly value: RichTextValue;
    readonly placeholder?: string;
    readonly sendButtonIcon?: string;
    readonly footerConfig?: WidgetFooterConfig;
    readonly inputConfig?: WidgetInputConfig;
    onSubmit: (msg: RichTextValue) => void;
    onFileUpload: (event: DragEvent<HTMLInputElement>) => void;
    addClass: string;
    suggestion?: SuggestionsItemData;
    onChange: (val: RichTextValue) => void;
    onSuggestionCommand?(cmd: SuggestionCommand): void;
    // onFocus: () => void,
}

const Input: FC<React.PropsWithChildren<Props>> = (props) => {
    const {
        value,
        placeholder,
        sendButtonIcon,
        suggestion,
        footerConfig,
        inputConfig,
        onChange,
        onSubmit,
        onSuggestionCommand,
    } = props;
    const [dragover, setDragover] = useState(false);

    function onDragOver(event: React.DragEvent<HTMLInputElement>) {
        setDragover(true);
        event.preventDefault();

        event.dataTransfer.dropEffect = "copy";
    }

    function onDrop(event: React.DragEvent<HTMLInputElement>) {
        setDragover(false);

        props.onFileUpload(event);
    }

    function onDragLeave() {
        setDragover(false);
    }

    const handleOnSubmit = useCallback(
        (event?: FormEvent) => {
            event && event.preventDefault();
            onSubmit(value); // send to the widget
        },
        [value, onSubmit]
    );

    const handleClear = useCallback(() => {
        onChange({
            text: "",
            formats: [],
        });
    }, [onChange]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (suggestion) {
                if (event.key === "End" || event.key === "ArrowRight" || event.key === "Enter") {
                    onChange(suggestion);
                    return;
                }
            }
            if (onSuggestionCommand) {
                if (event.key === "ArrowUp") {
                    onSuggestionCommand("prev");
                    event.preventDefault();
                } else if (event.key === "ArrowDown") {
                    onSuggestionCommand("next");
                    event.preventDefault();
                } else if (event.key === "Escape") {
                    onSuggestionCommand("cancel");
                    event.preventDefault();
                }
            }
        },
        [suggestion, onChange, onSuggestionCommand]
    );

    return (
        <div
            className={`xappw-input-container ${props.addClass} ${dragover ? "drag-drop-zone" : ""
                }`}
            aria-label={
                "To start typing click on rounded rectangle at the bottom of widget. To send your message click icon on right side of rounded rectangle at the bottom of widget" +
                    value.text
                    ? "To clear input field click on clear icon on the left of send button"
                    : ""
            }
            aria-hidden={false}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}>
            <form className="xappw-input-form" onSubmit={handleOnSubmit}>
                <RichInput
                    key="input"
                    id="chatWidgetInput"
                    className="xappw-input"
                    placeholder={placeholder ?? "type your question here"}
                    tabIndex={inputConfig?.tabIndex}
                    onInput={onChange}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    // onFocus={onFocus}
                    value={value}
                    spellCheck
                />
                <div className="xappw-input-form__buttons">
                    {value.text && (
                        <IconButton
                            icon={CloseIcon}
                            tabIndex={footerConfig?.clearButton?.tabIndex}
                            className="xappw-input-form__btn"
                            onClick={handleClear}
                        />
                    )}
                    <SendButton
                        className="xappw-input-form__btn"
                        sendButtonIcon={sendButtonIcon}
                        tabIndex={footerConfig?.sendButton?.tabIndex}
                        onClick={handleOnSubmit}
                    />
                </div>
            </form>
        </div>
    );
};

export default Input;
